import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from "react-router-dom";
import {
  ArrowBack,
} from "@mui/icons-material";
import { Button, Fab, Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  BasicItem, 
  Item, 
  Ammunition,
  Container,
  DeckItem,
  GemItem, 
  MagicItemKeyword
} from "@dndModels";
import { ItemType } from "@dndModels/Types";
import { makeStyles } from "@utilities/theme_utilities";
import { RARITY_NUM_TO_STR_MAP } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import LoadingBox from "@components/input/LoadingBox";
import ViewItem from "@dndComponents/display/ViewItem";
import DeleteButton from "@components/input/buttons/DeleteButton";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import CheckBox from "@components/input/CheckBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function ItemParseAdd() {
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [old_items, setOldItems] = useState<Item[]>([]);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [parse_string, setParseString] = useState("");
  const [processing, setProcessing] = useState(false);
  const [complete, setComplete] = useState(false);
  const [processing_message, setProcessingMessage] = useState("");
  const [items, setItems] = useState<(Item | MagicItemKeyword)[]>([]);
  const [expanded_item, setExpandedItem] = useState("");
  const [make_dominant, setMakeDominant] = useState(false);
  const [final_report, setFinalReport] = useState<string[]>([]);

  const parseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if(!fetched) {
      load();
    }
  });
  
  const load = () => {
    setLoading(true);
    api.getSetOfObjects({ data_types: ["item"]}).then(res => {
      setOldItems(res.item as Item[]);
      setLoading(false);
    });
    setFetched(true);
  }
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main
      },
    });
  });

  const { classes, cx } = useStyles();

  const submit = (error_messages: string[] = []) => {
    const next_item = items.find(o => o._id === "");
    if (next_item) {
      // if (make_dominant && next_item.variant_group.length > 0) {
      //   const max_dominance_level = Math.max(...next_item.variant_group.map(o => o.dominance_level));
      //   next_item.dominance_level = max_dominance_level + 1;
      // }
      if (next_item.parse_errors.length > 0) {
        error_messages.push(...next_item.parse_errors.map(o => {
          return `${next_item.name}: ${o}`;
        }));
      }
      setProcessingMessage(`Saving ${next_item.name}`);
      api.upsertObject(next_item.data_type, next_item).then(res => {
        console.log(res);
        if (res && res.body.id) {
          next_item._id = res.body.id;
        } else {
          next_item._id = "Error";
          error_messages.push(`${next_item.name} Save Failed`);
        }
        submit(error_messages);
      });
    } else {
      setFinalReport(error_messages);
      setProcessing(false);
      setComplete(true);
    }
  }

  const stripAttributes = (html: string): string => {
    // Create a temporary div to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Recursively strip attributes from all elements
    const stripFromNode = (node: Node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        // Remove all attributes
        while (element.attributes.length > 0) {
          element.removeAttribute(element.attributes[0].name);
        }
        // Process children
        Array.from(element.childNodes).forEach(stripFromNode);
      }
    };
    
    Array.from(temp.childNodes).forEach(stripFromNode);
    return temp.innerHTML;
  };

  const parse = () => {
    let elem = parseRef.current as HTMLElement;
    while (elem instanceof HTMLDivElement && elem.children.length < 2) {
      elem = elem.children[0] as HTMLElement;
    }
    
    const new_items: (Item | MagicItemKeyword)[] = [];
    let i = 0;
    
    // Find the first item (H3 tag)
    while (i < elem.children.length) {
      const child = elem.children[i];
      ++i;
      if (child instanceof HTMLElement && 
          child.tagName === "H3" && 
          (child.className.includes("compendium--magic-item") || 
           child.className.includes("heading-anchor"))) {
        --i; // Back up to process this item
        break;
      }
    }
    
    let current_item: Item | MagicItemKeyword | null = null;
    let item_type_rarity_line = "";

    const createItemByType = (item_type: ItemType) => {
      let new_item: Item | MagicItemKeyword;
      switch (item_type) {
        case "Armor":
          new_item = new MagicItemKeyword();
          new_item.base_item_types.push("Armor");
          break;
        case "Weapon":
          new_item = new MagicItemKeyword();
          new_item.base_item_types.push("Weapon");
          break;
        case "Ammunition":
          new_item = new Ammunition();
          break;
        case "Container":
          new_item = new Container();
          break;
        case "Deck":
          new_item = new DeckItem();
          break;
        case "Gem":
          new_item = new GemItem();
          break;
        default:
          new_item = new BasicItem();
          break;
      }
      if (new_item instanceof Item) {
        new_item.item_type = item_type;
      }
      return new_item;
    };

    const addItem = () => {
      if (current_item && current_item.name.length > 0) {
        const ci = current_item;
        const possible_variants = old_items.filter(o => o.name === ci.name);
        let variant_group_ids: string[] = [];
        for (const pv of possible_variants) {
          variant_group_ids.push(pv.variant_group_id);
          current_item.variant_ids = [...pv.variant_ids,...current_item.variant_ids];
        }
        variant_group_ids = common_util.unique_array(variant_group_ids);
        current_item.variant_ids = common_util.unique_array(current_item.variant_ids);
        if (variant_group_ids.length === 1) {
          current_item.variant_of_id = variant_group_ids[0];
          current_item.variant_group = possible_variants;
        }
        
        if (api.working_on_sb) {
          const sb_id = api.working_on_sb._id;
          current_item.source_books_old = [{ 
            id: api.working_on_sb._id, 
            type: api.working_on_sb.source_book_type, 
            third_party: api.working_on_sb.third_party 
          }];
          if (possible_variants.filter(o => o.source_book_ids.includes(sb_id)).length > 0) {
            current_item.parse_errors.push("Variant on Book Already Exists.");
          }
        }

        current_item.description = common_util.cleanSpaces(current_item.description);
        
        // Add implementation note at the end
        current_item.description += "\r\n\r\nNOTE: This hasn't been fully implemented yet. If you are using it and it could use some work, please make a bug report using the bug button in the bar at the top of the page.";
        
        // Parse charges if mentioned
        const desc_lower = current_item.description.toLowerCase();
        if (desc_lower.includes("has") && desc_lower.includes("charge")) {
          const charge_match = current_item.description.match(/has (\d+) charges?/i);
          if (charge_match) {
            current_item.charges = parseInt(charge_match[1]);
          }
        }
        
        // Parse refresh rules
        if (desc_lower.includes("regains") && desc_lower.includes("charge")) {
          if (desc_lower.includes("dawn") || desc_lower.includes("daily")) {
            current_item.charge_refresh_rule = "Long Rest";
            const regain_match = current_item.description.match(/regains (\d+)d(\d+)\s*\+?\s*(\d*) expended charges?/i);
            if (regain_match) {
              current_item.refresh_dice_count = parseInt(regain_match[1]);
              current_item.refresh_dice_size = parseInt(regain_match[2]);
              current_item.refresh_dice_flat = regain_match[3] ? parseInt(regain_match[3]) : 0;
            }
          }
        }
        
        new_items.push(current_item);
      }
    };

    while (i < elem.children.length) {
      const child = elem.children[i];
      ++i;
      
      if (!(child instanceof HTMLElement)) {
        continue;
      }

      // Skip aside tags and figures
      if (child.tagName === "ASIDE" || child.tagName === "FIGURE") {
        continue;
      }

      // Check if this is a new item (H3 tag)
      if (child.tagName === "H3" && 
          (child.className.includes("compendium--magic-item") || 
           child.className.includes("heading-anchor"))) {
        // Save previous item if exists
        addItem();
        
        // Start new item
        current_item = new BasicItem();
        current_item.name = child.textContent ? child.textContent.trim() : "";
        current_item.name = current_item.name.replace("'", "'");
        item_type_rarity_line = "";
        continue;
      }

      // If we have a current item, process its content
      if (current_item) {
        if (child.tagName === "P") {
          const text = child.textContent ? child.textContent.trim() : "";
          const inner_html = child.innerHTML;
          
          // Check if this is the item type/rarity line (usually in <em> tags)
          if (text && child.querySelector('em') && !item_type_rarity_line) {
            item_type_rarity_line = text;
            
            // Add this line to the description
            current_item.description += text + "\r\n\r\n";
            
            // Parse item type and rarity
            // Remove anything in parentheses for parsing
            const clean_text = text.replace(/\([^)]*\)/g, '').trim();
            const pieces = clean_text.split(",");
            
            if (pieces.length >= 2) {
              // Parse item type
              const type_str = pieces[0].trim();
              let detected_type: ItemType = "Other";
              
              if (type_str.includes("Weapon")) {
                detected_type = "Weapon";
              } else if (type_str.includes("Armor")) {
                detected_type = "Armor";
              } else if (type_str.includes("Potion")) {
                detected_type = "Potion";
              } else if (type_str.includes("Ring")) {
                detected_type = "Ring";
              } else if (type_str.includes("Rod")) {
                detected_type = "Rod";
              } else if (type_str.includes("Staff")) {
                detected_type = "Staff";
              } else if (type_str.includes("Wand")) {
                detected_type = "Wand";
              } else if (type_str.includes("Wondrous")) {
                detected_type = "Wondrous";
              } else if (type_str.includes("Scroll")) {
                detected_type = "Scroll";
              }
              
              // Create new item of the correct type if needed
              if (detected_type !== "Other") {
                const old_item = current_item;
                current_item = createItemByType(detected_type);
                current_item.name = old_item.name;
                current_item.description = old_item.description;
              }
              
              // Parse rarity
              const rarity_str = pieces[pieces.length - 1].trim().toLowerCase();
              if (rarity_str.includes("common")) {
                current_item.min_rarity = 0;
              } else if (rarity_str.includes("uncommon")) {
                current_item.min_rarity = 1;
              } else if (rarity_str.includes("very rare")) {
                current_item.min_rarity = 3;
              } else if (rarity_str.includes("rare")) {
                current_item.min_rarity = 2;
              } else if (rarity_str.includes("legendary")) {
                current_item.min_rarity = 4;
              } else if (rarity_str.includes("artifact")) {
                current_item.min_rarity = 5;
              }
              current_item.max_rarity = current_item.min_rarity;
              
              // Check for attunement in the original text (including parentheses)
              if (text.toLowerCase().includes("attunement")) {
                current_item.attunement = true;
              }
            }
          } else if (inner_html) {
            // This is a description paragraph
            // Strip attributes but keep tags
            const cleaned_html = stripAttributes(inner_html);
            current_item.description += cleaned_html + "\r\n\r\n";
          }
        }
      }
    }
    
    // Add the last item
    addItem();
    setItems(new_items);
  }

  const renderItems = () => {
    return (
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={0} direction="row">
          <Grid
            className="header centered"
            size={{
              xs: 12
            }}>Items Ready to Add</Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <CheckBox 
              name="Make Dominant"
              value={make_dominant}
              onChange={setMakeDominant}
            />
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={ processing }
              onClick={() => { setItems(items.filter(o => !o.parse_errors.includes("Variant on Book Already Exists."))); }}>
              Delete All Already in Source
            </Button>
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={ processing }
              onClick={() => {
                setProcessing(true);
                submit();
              }}>
              Save All
            </Button>
          </Grid>
          <Grid
            size={{
              xs: 3
            }}>
            <Button
              variant="contained"
              color="primary"
              disabled={ processing }
              onClick={() => { setItems([]); }}>
              Cancel
            </Button>
          </Grid>
          { items.map(item => {
            return (
              <Grid
                key={item.name}
                className="padded"
                size={{
                  xs: 12
                }}>
                <Grid container spacing={0} direction="row" className={cx(classes.root)}>
                  <Grid
                    onClick={() => { setExpandedItem(expanded_item === item.name ? "" : item.name); }}
                    size={{
                      xs: 4
                    }}>
                    <ViewItem item={item} />
                  </Grid>
                  <Grid
                    onClick={() => { setExpandedItem(expanded_item === item.name ? "" : item.name); }}
                    size={{
                      xs: 2
                    }}>
                    { item.item_type }
                  </Grid>
                  <Grid
                    onClick={() => { setExpandedItem(expanded_item === item.name ? "" : item.name); }}
                    size={{
                      xs: 2
                    }}>
                    { RARITY_NUM_TO_STR_MAP[item.min_rarity] }
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <DisplayObjects type="item" ids={item.variant_ids} />
                  </Grid>
                  <Grid
                    size={{
                      xs: 2
                    }}>
                    <DeleteButton
                      onClick={() => {
                        if (expanded_item === item.name) {
                          setExpandedItem("");
                        }
                        setItems(items.filter(o => o.name !== item.name));
                      }}
                    />
                  </Grid>
                  { item.parse_errors.map((error, key) => {
                    return (
                      <Grid
                        key={key}
                        className="small_label red"
                        size={{
                          xs: 12
                        }}>
                        { error }
                      </Grid>
                    );
                  })}
                  { expanded_item === item.name &&
                    <Grid
                      className="padded"
                      size={{
                        xs: 12
                      }}>
                      <div className="blackBackground">
                        <DisplayStringWithReplacements display_string={item.description} />
                      </div>
                    </Grid>
                  }
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  }

  const renderParser = () => {
    return (
      <>
        <Grid
          size={{
            xs: 9
          }}>
          <StringBox 
            name="Parse String"
            value={parse_string}
            onBlur={setParseString}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <Button
            variant="contained"
            color="primary"
            disabled={ processing || parse_string.length === 0 }
            onClick={parse}>
            Parse
          </Button>
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <div ref={parseRef} dangerouslySetInnerHTML={{ __html: parse_string }}></div>
        </Grid>
      </>
    );
  }

  if (!fetched || loading) {
    const height = window.innerHeight - 81;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth - 1} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <Tooltip title={`Back to Items`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                setNavigateTo(`/item`);
              }}>
              <ArrowBack/>
            </Fab>
          </Tooltip>&nbsp;
          <span className="header">
            Parse Items
          </span>
        </Grid>
        { processing &&
          <Grid
            className="centered"
            size={{
              xs: 12
            }}>
            <LoadingBox message={processing_message} height={200} />
          </Grid>
        }
        { !processing && !complete && 
          <>
            { items.length > 0 ? renderItems() : renderParser() }
          </>
        }
        { complete &&
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Log"
              value={final_report.join("\r\n")}
              multiline
            />
          </Grid>
        }
      </Grid>
    );
  }
}